import React, { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import ProductImageFallback from "../../../CommonComponents/ProductImageFallback";

function CartItemRow({ item, rowNumber, onEdit, onDelete, formatPrice, highlightKey }) {
    const [highlight, setHighlight] = useState(false);
    const [imageError, setImageError] = useState(false);
    const imageUrl = item.image || item.image_url || item.imageUrl || null;

    useEffect(() => {
        // If this row matches the highlightKey, animate
        if (highlightKey === (item.lineId || item.id)) {
            setHighlight(true);
            const timer = setTimeout(() => setHighlight(false), 800); // highlight duration
            return () => clearTimeout(timer);
        }
    }, [highlightKey, item.lineId, item.id]);

    return (
        <div
            className="flex items-center justify-between gap-2 py-2 last:border-none transition-colors duration-500 border-b border-dashed border-gray-300"
            style={{
                background: highlight ? 'var(--color-hover)' : 'transparent',
            }}
        >
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold leading-none text-slate-500 shadow-sm"
                    title={`Cart row ${rowNumber}`}
                    aria-label={`Cart row ${rowNumber}`}
                >
                    {rowNumber}
                </span>
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
                    {imageUrl && !imageError ? (
                        <img
                            src={imageUrl}
                            alt={item.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <ProductImageFallback className="h-full w-full" aria-label="No product image" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <p
                        className="line-clamp-2 text-sm font-semibold leading-5"
                        style={{ color: 'var(--color-text)' }}
                    >
                        {item.name}
                    </p>
                    <p className="text-xs font-semibold text-slate-500">
                        Qty {item.qty}
                    </p>
                </div>
            </div>
            <div className="flex shrink-0 items-center space-x-1">
                <p
                    className="min-w-[48px] text-right text-sm font-semibold"
                    style={{ color: 'var(--color-text)' }}
                >
                    {formatPrice(item.price * item.qty)}
                </p>
                <button
                    onClick={() => onEdit(item)}
                    className="transition-colors p-1 rounded focus:outline-none hover:bg-gray-100"
                    title="Edit item"
                    style={{ color: '#6b7280' }}
                    onMouseOver={e => e.currentTarget.style.color = 'var(--color-primary)'}
                    onMouseOut={e => e.currentTarget.style.color = '#6b7280'}
                >
                    <Pencil size={14} />
                </button>
                <button
                    onClick={onDelete}
                    className="transition-colors p-1 rounded focus:outline-none hover:bg-red-50"
                    title="Remove item"
                    style={{ color: '#6b7280' }}
                    onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                    onMouseOut={e => e.currentTarget.style.color = '#6b7280'}
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

// At the bottom of src/components/POS/cart/CartItemRow.js

export default React.memo(
    CartItemRow,
    (prev, next) =>
        prev.item.qty === next.item.qty &&
        prev.item.price === next.item.price &&
        prev.item.name === next.item.name &&
        (prev.item.image || prev.item.image_url || prev.item.imageUrl || null) === (next.item.image || next.item.image_url || next.item.imageUrl || null) &&
        prev.rowNumber === next.rowNumber &&
        prev.highlightKey === next.highlightKey
);
